import { useDroppable } from "@dnd-kit/core";
import { Plus, Search } from "lucide-react";
import { memo, useState } from "react";
import { CharacterDialog } from "@/shared/components/CharacterDialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { RAID_ROLE_LABELS, type RaidRole } from "@/shared/lib/roles";
import { cn } from "@/shared/lib/utils";
import { WOW_CLASS_LABELS, type WowClass } from "@/shared/lib/wowClass";
import { POOL_DROP_ID, type PoolPlayer } from "../splits.logic";
import type { BoardCharacter } from "../splits.types";
import { DraggableCharacterCard } from "./CharacterCard";

// Left rail of unassigned characters: a summary header, a search box filtering on player name,
// role and class, and a dense two-column draggable chip grid with a fixed height + internal
// scroll. The whole rail is one droppable ("pool"). "+ Lägg till karaktär" opens the shared
// create dialog with a player-select.
// Memoized alongside SplitColumn so remote cursor frames don't re-render the pool (see SplitsBoard).
export const UnassignedPool = memo(function UnassignedPool({
  unassigned,
  totalCharacters,
  players,
  remoteDraggingIds,
}: {
  unassigned: BoardCharacter[];
  totalCharacters: number;
  players: PoolPlayer[];
  remoteDraggingIds?: Set<string>;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: POOL_DROP_ID });
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const q = query.trim().toLowerCase();
  const visible = q
    ? unassigned.filter((c) => {
        const role = c.raidRole ? RAID_ROLE_LABELS[c.raidRole as RaidRole] : "";
        const cls = WOW_CLASS_LABELS[c.wowClass as WowClass] ?? "";
        return (
          c.playerName.toLowerCase().includes(q) ||
          role.toLowerCase().includes(q) ||
          cls.toLowerCase().includes(q)
        );
      })
    : unassigned;

  return (
    <>
      <div
        ref={setNodeRef}
        className={cn(
          "flex w-64 shrink-0 flex-col rounded-md border border-border",
          isOver && "bg-muted/50",
        )}
      >
        <div className="space-y-2 border-b border-border p-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">
              {unassigned.length} utan split
            </span>
            <span className="text-xs text-muted-foreground">
              av {totalCharacters} totalt
            </span>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sök namn, roll eller klass"
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setDialogOpen(true)}
          >
            <Plus />
            Lägg till karaktär
          </Button>
        </div>

        <div className="max-h-[calc(100dvh-12rem)] min-h-16 overflow-y-auto p-1.5">
          {visible.length === 0 ? (
            <p className="px-1 py-2 text-center text-xs text-muted-foreground/60">
              Inga karaktärer
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {visible.map((c) => (
                <DraggableCharacterCard
                  key={c.id}
                  character={c}
                  dense
                  remoteDragging={remoteDraggingIds?.has(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CharacterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        players={players}
      />
    </>
  );
});
